/**
 * sessionStorage helpers for navigation persistence.
 *
 * Separated from the reducer so the reducer stays pure and testable.
 * All helpers swallow storage exceptions (quota, privacy mode): navigation
 * must never fail because storage is unavailable.
 *
 * Note: this module is intentionally View-type-agnostic. sessionStorage stores
 * strings; narrowing back to View happens at the navigation-state caller.
 * Importing View here would create a navigation-state ↔ storage cycle.
 */

export const VIEW_STORAGE_KEY = 'joshua_current_view';
export const OBRA_ID_STORAGE_KEY = 'joshua_selected_obra_id';
export const FRESH_LOGIN_KEY = 'joshua_fresh_login';

/**
 * Views that require a selected ID to render meaningfully. After F5 we fall
 * back to home rather than restore these, because the ID is not reliably
 * persisted across a reload.
 */
export const VIEWS_REQUIRING_CONTEXT: readonly string[] = [
  'proposta-details',
  'banco-details',
  'conta-details',
  'centro-custo-details',
  'departamento-details',
  'estado-details',
  'cidade-details',
  'tipo-ambiente-details',
  'tipo-ponto-details',
  'grupo-tipo-ponto-details',
] as const;

export function isFreshLogin(): boolean {
  try {
    return sessionStorage.getItem(FRESH_LOGIN_KEY) !== null;
  } catch {
    return false;
  }
}

export function readPersistedView(): string | null {
  try {
    return sessionStorage.getItem(VIEW_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writePersistedView(view: string): void {
  try {
    sessionStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Storage quota or privacy mode — next render will retry.
  }
}

export function readPersistedObraId(): string | null {
  try {
    return sessionStorage.getItem(OBRA_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writePersistedObraId(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(OBRA_ID_STORAGE_KEY, id);
    else sessionStorage.removeItem(OBRA_ID_STORAGE_KEY);
  } catch {
    // swallow
  }
}
